import { DeleteOutlined } from "@ant-design/icons";
import origDrop from "@assets/redesign/icons/drop.svg";
import wlpDrop from "@components/WLP202603/assets/redesign/icons/drop.svg";
import Loader from "@components/global/Loader/Loader";
import Icon from "@components/global/new/Icon/Icon";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { MODALS_ENUM } from "@constants/Modals";
import mainLocationsStore from "@mobx/store/locations";
import { DRAGGING_TYPES3, IDragRackItemData3 } from "@mobx/store/locations/types";
import unallocatedMinersStore from "@mobx/store/locations/unallocated";
import { useToasts } from "@utils/hooks/useToasts";
import { makeNotifs } from "@utils/makeNotifs";
import { Collapse } from "antd";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { DragEvent, UIEvent, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { show as showModal } from "redux-modal";
import { WLP202603 } from "../../../config";
import Filters3 from "../Filters3";
import UnrackItem3 from "../UnrackItem3";
import { dropRackItemToUnallocated } from "./dropRackItem";
import styles from "./UnallocatedMiners.module.scss";

const drop = WLP202603 ? wlpDrop : origDrop;

export interface IUnallocatedMiners3Props {
  readonly show?: boolean;
}

type DragEventType3 = DragEvent<HTMLDivElement>;

const useControl = (
  props: IUnallocatedMiners3Props
) => {
  const miners = unallocatedMinersStore.miners;
  const locationStore = mainLocationsStore.activeLocation;
  const buildingStore = locationStore?.activeBuilding;
  const dispatch = useDispatch();
  const { error } = useToasts();
  const { errors } = makeNotifs();
  const [show, setShow] = useState<boolean>(props.show || false);
  const [showDragOverlay, setShowDragOverlay] = useState<boolean>(false);
  const [search, setSearch] = useState<string>();
  const [status, setStatus] = useState<MINER_STATUSES_E>();
  const stores = useMemo(() => miners.map((m, i) => unallocatedMinersStore.createItemStore(m, i)), [miners])
  const [offset, setOffset] = useState<number>(0);
  const curOffset = useRef(0);

  const onShow = () => setShow((prev) => !prev);
  const showLogs = () => dispatch(showModal(MODALS_ENUM.LOGS_MODAL));
  const onClickSearch = () => unallocatedMinersStore.search(search, status);

  useEffect(() => {
    if (!show) return;
    onClickSearch();
  }, [show, search, status]);

  useEffect(() => {
    if (offset <= curOffset.current) return;
    ((async () => {
      const offs = await unallocatedMinersStore.fetchMore();
      if (offs >= 0) curOffset.current = offs;
    })());
  }, [offset]);

  const onDragEnter = (e: DragEventType3) => {
    if (e.dataTransfer.types[0] !== DRAGGING_TYPES3.RACK_ITEM) return;
    setShowDragOverlay(true);
  };

  const onDragLeave = () => {
    setTimeout(() => setShowDragOverlay(false), 50);
  };

  const onDropRackItem = async (e: DragEventType3) => {
    if (e.dataTransfer.types[0] !== DRAGGING_TYPES3.RACK_ITEM) return;

    try {
      const rackItemData = JSON.parse(
        e.dataTransfer.getData(DRAGGING_TYPES3.RACK_ITEM)
      ) as IDragRackItemData3;
      window.TRANSFER_RACK_ITEM = undefined;

      await dropRackItemToUnallocated({
        buildingStore,
        onMergedError: () => error(errors.locations__unnalocated_unmerge),
        rackItemData,
      });
    } finally {
      window.TRANSFER_RACK_ITEM = undefined;
      setShowDragOverlay(false);
    }
  };

  return {
    miners,
    show,
    showDragOverlay,
    offset,
    search,
    status,
    loading: unallocatedMinersStore.isLoading,
    stores,

    onShow,
    showLogs,
    onDragEnter,
    onDragLeave,
    onDropRackItem,
    setOffset,
    onSearch(s?: string, f?: string) {
      if (f === undefined) setStatus(undefined)
      else {
        const k = f as keyof typeof MINER_STATUSES_E;
        setStatus(MINER_STATUSES_E[k]);
      }
      setSearch(s);
    },
    onClickSearch,
  };
};

const UnallocatedMiners3 = observer((props: IUnallocatedMiners3Props) => {
  const { formatMessage } = useIntl();
  const scrollContainer = useRef<HTMLDivElement>(null);

  const control = useControl(props);

  useEffect(() => {
    if (!control.show) control.setOffset(0);
  }, [control.show]);

  const onScroll = (e: UIEvent<HTMLDivElement, globalThis.UIEvent>) => {
    if (
      e.currentTarget.scrollHeight - e.currentTarget.clientHeight ===
      e.currentTarget.scrollTop
    ) {
      control.setOffset((prev) => prev + 100);
    }
  };

  return (
    <Collapse
      className={styles.collapse}
      collapsible="icon"
      ghost
      activeKey={control.show ? "1" : ""}
      items={[
        {
          key: "1",
          label: <div className={styles.header} onClick={control.onShow}>
            <h5 className={styles.head}>
              {formatMessage({ id: "location.unnalocated.title" })}
            </h5>
            <Icon
              className={cn(styles.collapseButton, {[styles.collapsed]: !control.show})}
              src={drop}
              noHover
            />
          </div>,
          children: <div className={styles.wrapper}>
            {/* <UnnalocatedFilter /> */}
            <Filters3 onSearch={control.onSearch} />
            <div className={styles.content} onDragEnter={control.onDragEnter}>
              {control.show && control.loading && (
                <Loader size={24} style={{ zIndex: 10 }} />
              )}
              {control.showDragOverlay && (
                <div
                  className={styles.backOverlay}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={control.onDropRackItem}
                  onDragLeave={control.onDragLeave}
                >
                  <p>
                    <DeleteOutlined
                      className={styles.icon}
                      size={24}
                      rev={undefined}
                    />
                  </p>
                </div>
              )}
              <div
                className={styles.miners}
                ref={scrollContainer}
                onScroll={onScroll}
              >
                {control.miners.length === 0 ? (
                  <div className={styles.empty}>
                    {formatMessage({ id: "location.unnalocated.empty" })}
                  </div>
                ) : (
                  control.stores.map((s, i) => (
                    <UnrackItem3 itemStore={s} key={s.item.id} />
                  ))
                )}
              </div>
    
              {/* Это для логов. не удалять */}
              {/* <div className={styles.log_wrapper}>
                <div className={styles.logs}>
                  <h5 className={styles.head}>The event log</h5>
                  <div className={styles.log}>
                    2023-02-20 | 13:00 Name change on placement 068A70 {">>"}{" "}
                    002CO1-007A15
                  </div>
                </div>
                <SmallLink className={styles.preview} onClick={control.showLogs}>
                  preview
                  <Icon src={view} />
                </SmallLink>
              </div> */}

            </div>
          </div>,
          showArrow: false,
        },
      ]}
    />
  );
});

UnallocatedMiners3.displayName = "UnallocatedMiners3";

export default UnallocatedMiners3;
